import React from 'react';
import PropTypes from 'prop-types';
import { View, FlatList } from 'react-native';
import { colors } from '@styleseat/brand';
import analytics from '../../../../modules/analytics';
import { ProviderProductPropType } from '../ProfilePropTypes';
import {
  Button,
  Stack,
  BodyText,
} from '../../../ui';
import ModalTrigger from '../../../ui/ModalTrigger';
import ProviderProductCard from '../ProviderProductCard/ProviderProductCard';
import CardShopButton from '../ProviderProductCard/CardShopButton';
import CardRequestButton from '../ProviderProductCard/CardRequestButton';
import CardEditDeleteButtons from '../ProviderProductCard/CardEditDeleteButtons';
import { ProviderProductForm } from '../ProviderProductForm/ProviderProductForm';
import './ProviderProductsList.module.scss';

function ProductCard({
  product,
  isEditMode,
  onShopItem,
  onEditItem,
  onRequestItem,
  onDeleteItem,
}) {
  let renderActions = null;
  if (isEditMode) {
    renderActions = () => (
      <CardEditDeleteButtons
        product={product}
        onEditItem={onEditItem}
        onDelete={() => onDeleteItem(product)}
      />
    );
  } else if (product.link) {
    renderActions = () => <CardShopButton onShop={() => onShopItem(product)} />;
  } else {
    renderActions = () => <CardRequestButton onRequest={() => onRequestItem(product)} />;
  }
  return <ProviderProductCard product={product} renderActions={renderActions} />;
}

ProductCard.propTypes = {
  product: ProviderProductPropType.isRequired,
  isEditMode: PropTypes.bool.isRequired,
  onShopItem: PropTypes.func.isRequired,
  onRequestItem: PropTypes.func.isRequired,
  onDeleteItem: PropTypes.func.isRequired,
  onEditItem: PropTypes.func.isRequired,
};

/**
 * Displays a list containing a product card for each provider product
 * @param products
 * @param onShopItem
 * @param onRequest
 * @returns {*}
 * @constructor
 */
export default function ProviderProductsList({
  products,
  isEditMode,
  showGiftCard,
  onShopItem,
  onRequestItem,
  onDeleteItem,
  onAddItem,
  onEditItem,
  showAddProduct,
}) {
  const hasProducts = products.length || showGiftCard;
  return (
    <Stack space={8}>
      {hasProducts && (
        <React.Fragment>
          <FlatList
            data={products}
            renderItem={({ item }) => (
              <ProductCard
                product={item}
                onDeleteItem={onDeleteItem}
                onEditItem={onEditItem}
                onRequestItem={onRequestItem}
                onShopItem={onShopItem}
                isEditMode={isEditMode}
              />
            )}
            keyExtractor={item => String(item.id)}
            ItemSeparatorComponent={() => <View style={{ width: '100%', height: 4 }} />}
          />
        </React.Fragment>
      )}
      {isEditMode && (
        <ModalTrigger
          initialOpen={showAddProduct}
          renderModal={onClose => (
            <ProviderProductForm
              onClose={onClose}
              onSaveProduct={onAddItem}
              product={null}
            />
          )}
          renderTrigger={onOpen => (
            <Stack
              alignItems="center"
              padding={20}
              space={8}
              color={colors.whiteWash}
              borderStyle="solid"
              borderWidth={1}
              borderColor={colors.lightGrey}
            >
              <BodyText>Add Products to your profile</BodyText>
              <Button
                testID="addProductsButton"
                onClick={() => {
                  onOpen();
                  analytics.track('pro_clicked_add_product_button');
                }}
                appearance="primary-neue"
              >
                Add Products
              </Button>
            </Stack>
          )}
        />
      )}
    </Stack>
  );
}

ProviderProductsList.propTypes = {
  products: PropTypes.arrayOf(ProviderProductPropType),
  isEditMode: PropTypes.bool,
  showGiftCard: PropTypes.bool,
  onShopItem: PropTypes.func,
  onRequestItem: PropTypes.func,
  onDeleteItem: PropTypes.func,
  onAddItem: PropTypes.func.isRequired,
  onEditItem: PropTypes.func.isRequired,
  showAddProduct: PropTypes.bool,
};

ProviderProductsList.defaultProps = {
  products: [],
  isEditMode: false,
  showGiftCard: false,
  onShopItem: () => { },
  onRequestItem: () => { },
  onDeleteItem: () => { },
  showAddProduct: false,
};
